import { useState, useMemo } from 'react';

export default function useSortableTable<T>(
  data: T[],
  defaultField: keyof T | string,
  defaultDir: 'asc' | 'desc' = 'asc',
  customComparators?: Record<string, (a: T, b: T) => number>,
) {
  const [sortField, setSortField] = useState<keyof T | string>(defaultField);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultDir);

  const handleSort = (field: keyof T | string) => {
    setSortDir(sortField === field && sortDir === 'asc' ? 'desc' : 'asc');
    setSortField(field);
  };

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const custom = customComparators?.[sortField as string];
      if (custom) {
        const cmp = custom(a, b);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const aVal = a[sortField as keyof T];
      const bVal = b[sortField as keyof T];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDir, customComparators]);

  return { sorted, sortField, sortDir, handleSort };
}
